import {
  AlertCircle,
  Clock,
  ShieldCheck,
  CreditCard,
  ArrowRight,
  Pause,
  X
} from 'lucide-react';
import { useProfile } from '../../../../../context/profile/ProfileContext';

const SubscriptionBanners = () => {
  const {
    activeTab,
    subscription,
    latestRequest,
    dismissedRejectedId,
    handleDismissRejectedBanner,
    handleAuthorizePendingPlan,
    isAuthorizingMandate,
    handleResumeSubscription,
    setShowPmModal,
    formatDate,
    navigate
  } = useProfile();

  if (activeTab !== 'subscription' || !subscription) return null;

  return (
    <div className="space-y-4">
          <div className="space-y-4">
            {/* Rejected Request Alert Banner */}
            {latestRequest && latestRequest.status === 'rejected' && dismissedRejectedId !== latestRequest._id && (
              <div className="flex items-start justify-between gap-3.5 p-4 rounded-2xl bg-rose-50/90 border border-rose-200 text-rose-900 text-xs sm:text-sm shadow-xs animate-modal-entry">
                <div className="flex gap-3 items-start">
                  <div className="w-8 h-8 rounded-xl bg-rose-100 text-rose-700 flex items-center justify-center shrink-0 mt-0.5">
                    <AlertCircle size={18} />
                  </div>
                  <div>
                    <b className="block text-rose-950 font-bold text-sm">
                      {latestRequest.type === 'cancellation'
                        ? 'Cancellation request declined'
                        : latestRequest.type === 'downgrade'
                          ? 'Downgrade request declined'
                          : 'Billing cycle switch declined'}
                    </b>
                    <p className="text-xs text-rose-800 leading-relaxed mt-0.5">
                      {latestRequest.admin_notes || 'Your request was reviewed and could not be processed at this time.'} Your current plan ({subscription?.plan_id?.name || 'Current'} Tier) remains active and protected.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleDismissRejectedBanner(latestRequest._id)}
                  className="p-1 hover:bg-rose-100/80 rounded-lg text-rose-500 hover:text-rose-800 transition-colors cursor-pointer shrink-0"
                  title="Dismiss alert"
                >
                  <X size={16} />
                </button>
              </div>
            )}

            {/* Cancellation Request Pending Banner */}
            {subscription?.cancellation_requested && (!latestRequest || latestRequest.status === 'pending') && (
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-amber-50 via-slate-50 to-amber-50/50 border border-amber-200/90 p-5 shadow-xs transition-all animate-modal-entry">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-2xl bg-amber-500/10 text-amber-800 flex items-center justify-center shrink-0 border border-amber-200/80 shadow-2xs">
                      <Clock size={20} className="text-amber-600 animate-pulse" />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <h4 className="font-bold text-sm text-slate-900 tracking-tight">
                          Cancellation Request Pending Review
                        </h4>
                        <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider text-amber-800 bg-amber-100 px-2.5 py-0.5 rounded-full border border-amber-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-ping" />
                          In Review
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        Your cancellation request has been logged. Our relationship team will review your account and coordinate with you within 24–48 hours.
                      </p>
                    </div>
                  </div>

                  <div className="shrink-0 sm:text-right border-t sm:border-t-0 border-amber-100 pt-2 sm:pt-0">
                    <span className="text-[11px] font-semibold text-amber-800 bg-white border border-amber-200/80 px-3 py-1.5 rounded-xl shadow-2xs inline-flex items-center gap-1.5">
                      <Clock size={13} className="text-amber-600" />
                      Expected resolution: 24–48 hrs
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Plan Change Pending Approval Banner */}
            {subscription?.pending_change?.status === 'pending_approval' && (!latestRequest || latestRequest.status === 'pending') && (
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-sky-50 via-slate-50 to-sky-50/50 border border-sky-200/90 p-5 shadow-xs transition-all animate-modal-entry">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-2xl bg-sky-500/10 text-sky-800 flex items-center justify-center shrink-0 border border-sky-200/80 shadow-2xs">
                      <Clock size={20} className="text-sky-600 animate-pulse" />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <h4 className="font-bold text-sm text-slate-900 tracking-tight">
                          Plan Change Request Pending Review
                        </h4>
                        <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider text-sky-800 bg-sky-100 px-2.5 py-0.5 rounded-full border border-sky-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-ping" />
                          In Review
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed">
                        Your request to switch to <b className="text-slate-900 font-semibold">{subscription?.pending_change?.target_plan_id?.name || 'New Plan'} ({subscription?.pending_change?.target_billing_interval === 'year' ? 'Annual' : 'Monthly'})</b> is currently in review by our team.
                      </p>
                    </div>
                  </div>

                  <div className="shrink-0 sm:text-right border-t sm:border-t-0 border-sky-100 pt-2 sm:pt-0">
                    <span className="text-[11px] font-semibold text-sky-800 bg-white border border-sky-200/80 px-3 py-1.5 rounded-xl shadow-2xs inline-flex items-center gap-1.5">
                      <Clock size={13} className="text-sky-600" />
                      Expected resolution: 24–48 hrs
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Plan Change Approved - Mandate Authorization Pending Banner */}
            {['approved', 'mandate_pending'].includes(subscription?.pending_change?.status) && (
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-teal-900 via-[#067978] to-teal-800 p-5 sm:p-6 text-white shadow-xl shadow-teal-950/15 border border-teal-600/30 animate-modal-entry">
                {/* Background decorative glows */}
                <div className="absolute -top-12 -right-12 w-44 h-44 bg-white/10 rounded-full blur-2xl pointer-events-none" />
                <div className="absolute -bottom-10 -left-10 w-36 h-36 bg-emerald-400/15 rounded-full blur-xl pointer-events-none" />

                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-2xl bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center shrink-0 shadow-inner">
                      <ShieldCheck className="text-emerald-300" size={24} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <h4 className="text-base font-bold text-white tracking-tight">
                          {subscription?.pending_change?.custom_price ? 'Switch to Custom Plan' : 'Plan Change Approved by Trrip'}
                        </h4>
                      </div>
                      <p className="text-xs text-teal-100/90 mt-1 leading-relaxed max-w-xl">
                        {subscription?.pending_change?.custom_price ? (
                          <>
                            A custom plan has been configured for your agency (₹{subscription?.pending_change?.custom_price}/month + 18% GST). Click below to authorize your mandate and switch to your custom plan.
                          </>
                        ) : (
                          <>
                            Your switch to <b className="text-white font-bold">{subscription?.pending_change?.target_plan_id?.name || 'New Plan'} ({subscription?.pending_change?.target_billing_interval === 'year' ? 'Annual' : 'Monthly'})</b> is approved. Click below to authorize your new payment mandate to activate the plan.
                          </>
                        )}
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={handleAuthorizePendingPlan}
                    disabled={isAuthorizingMandate}
                    className="px-5 py-2.5 bg-white hover:bg-teal-50 text-[#067978] text-xs font-black rounded-xl shadow-lg shadow-black/10 hover:shadow-xl transition-all flex items-center justify-center gap-2 shrink-0 cursor-pointer active:scale-95 disabled:opacity-60"
                  >
                    {isAuthorizingMandate ? (
                      <span className="w-4 h-4 border-2 border-[#067978] border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <CreditCard size={15} className="text-[#067978]" />
                        <span>{subscription?.pending_change?.custom_price ? 'Switch to Custom Plan' : 'Authorize & Activate Plan'}</span>
                        <ArrowRight size={14} className="text-[#067978]" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Active Custom Plan / Mandate Completed Banner */}
            {subscription?.plan_type === 'custom' && subscription?.status === 'active' && !subscription?.pending_change && (
              <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#067978] via-teal-800 to-emerald-900 p-5 sm:p-6 text-white shadow-xl border border-teal-500/30 animate-fadeIn">
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-2xl bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center shrink-0 shadow-inner">
                      <ShieldCheck className="text-emerald-300" size={24} />
                    </div>
                    <div>
                      <h4 className="text-base font-bold text-white tracking-tight">
                        Custom Plan Active &amp; Protected
                      </h4>
                      <p className="text-xs text-teal-100/90 mt-1 leading-relaxed max-w-xl">
                        Your custom agency deal (₹{subscription?.custom_price || subscription?.current_amount}/month + 18% GST) has been successfully authorized and is active.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="px-3.5 py-1.5 bg-emerald-400/20 text-emerald-200 border border-emerald-300/30 text-xs font-semibold rounded-xl">
                      ✓ Mandate Authorized &amp; Active
                    </span>
                  </div>
                </div>
              </div>
            )}

            {subscription?.status === 'cancelled' && !subscription?.cancellation_requested && (
              <div className="flex gap-3 align-start p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-sm">
                <AlertCircle className="text-amber-600 mt-0.5 shrink-0" size={18} />
                <div className="flex-1">
                  <b>Subscription cancelled.</b> {subscription?.plan_id?.name || 'Pro'} stays active until <b>{formatDate(subscription?.end_date || subscription?.nextBillingAt)}</b>. After that you lose commission earning, live location and notifications.{' '}
                  <button
                    onClick={() => {
                      const activePlanId = subscription?.plan_id?._id || subscription?.plan_id;
                      if (activePlanId) {
                        navigate(`/checkout?plan=${activePlanId}&interval=${subscription?.billing_interval || 'month'}&from=profile&mode=reactivate`);
                      } else {
                        handleResumeSubscription();
                      }
                    }}
                    className="underline font-bold text-amber-950 hover:text-black cursor-pointer"
                  >
                    Reactivate now
                  </button>
                </div>
              </div>
            )}

            {subscription?.status === 'paused' && (
              <div className="flex gap-3 align-start p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-sm">
                <Pause className="text-amber-600 mt-0.5 shrink-0" size={18} />
                <div className="flex-1">
                  <b>Subscription paused.</b> Billing is stopped and you cannot create new vouchers until <b>{formatDate(subscription?.paused_until)}</b>.{' '}
                  <button onClick={handleResumeSubscription} className="underline font-bold text-amber-950 hover:text-black">Resume subscription</button>
                </div>
              </div>
            )}

            {subscription?.status === 'payment_failed' && (
              <div className="flex gap-3 align-start p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-900 text-sm">
                <AlertCircle className="text-rose-600 mt-0.5 shrink-0" size={18} />
                <div className="flex-1">
                  <b>Payment failed.</b> We'll retry automatic debit soon. Update your payment method to avoid losing access.{' '}
                  <button onClick={() => setShowPmModal(true)} className="underline font-bold text-rose-950 hover:text-black">Update method</button>
                </div>
              </div>
            )}
          </div>
    </div>
  );
};

export default SubscriptionBanners;
